import React from 'react';
import { 
  Shield, 
  Stethoscope, 
  Brain, 
  Activity, 
  Code, 
  Building2
} from 'lucide-react';
import { ScrollReveal, StaggerContainer, StaggerItem } from '@/components/ui/ScrollReveal';
import { Container, Section } from '@/components/layout/Container';
import { ServiceCard } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

const services = [
  {
    icon: <Shield className="w-6 h-6" />,
    title: 'Medical Risk Advisory',
    description: 'Strategic risk assessment for insurance providers, medical evacuation operators, and international risk assessment firms.',
    details: ['Case review & due diligence', 'Emergency preparedness', 'International risk protocols'],
  },
  {
    icon: <Stethoscope className="w-6 h-6" />,
    title: 'Clinical Due Diligence',
    description: 'Comprehensive medical case review and clinical workflow optimization for private healthcare systems.',
    details: ['Clinical workflow audit', 'Quality assurance', 'Process optimization'],
  },
  {
    icon: <Brain className="w-6 h-6" />,
    title: 'AI Integration Strategy',
    description: 'Bridge clinical and engineering teams for successful healthcare AI implementation.',
    details: ['Technical-medical bridging', 'AI workflow design', 'Implementation roadmap'],
  },
  {
    icon: <Activity className="w-6 h-6" />,
    title: 'Emergency Preparedness',
    description: 'Design and evaluation of emergency response systems and medical evacuation protocols.',
    details: ['Response protocols', 'Crisis management', 'Training programs'],
  },
  {
    icon: <Code className="w-6 h-6" />,
    title: 'Health-Tech Development',
    description: 'Technical advisory for MedTech companies and digital health platforms.',
    details: ['Architecture review', 'Compliance guidance', 'Security assessment'],
  },
  {
    icon: <Building2 className="w-6 h-6" />,
    title: 'Board-Level Advisory',
    description: 'Strategic medical advisory at the executive and board level for healthcare organizations.',
    details: ['Governance advisory', 'Risk management', 'Strategic planning'],
  },
];

const targetOrganizations = [
  'Insurance Providers',
  'Medical Evacuation Operators',
  'International Risk Assessment Firms',
  'Private Healthcare Systems',
  'MedTech Companies',
  'Digital Health Platforms',
  'AI-Health Developers',
  'Healthcare Infrastructure Builders',
];

export const AdvisoryServices: React.FC = () => {
  return (
    <Section id="services">
      <Container>
        {/* Section Header */}
        <ScrollReveal className="text-center mb-16">
          <span className="text-caption text-apple-blue uppercase tracking-wider font-medium">
            Advisory Services
          </span>
          <h2 className="text-h1 md:text-display-m font-bold mt-4 mb-6">
            Strategic <span className="gradient-text">Engagement</span>
          </h2>
          <p className="text-body-l text-text-secondary max-w-2xl mx-auto">
            Each engagement is scoped around your actual problem —
            not a framework borrowed from a different sector.
          </p>
        </ScrollReveal>

        {/* Services Grid */}
        <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {services.map((service, i) => (
            <StaggerItem key={service.title}>
              <ServiceCard
                icon={service.icon}
                title={service.title}
                description={service.description}
                details={service.details}
                delay={i * 100}
              />
            </StaggerItem>
          ))}
        </StaggerContainer>

        {/* Target Organizations */}
        <ScrollReveal>
          <div className="bg-bg-tertiary rounded-3xl border border-border-subtle p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-h3 font-semibold mb-4">
                  Who I Work With
                </h3>
                <p className="text-body text-text-secondary mb-6">
                  I take on a limited number of engagements at a time, which means
                  the work gets real attention. Active across healthcare, health technology,
                  insurance, and risk operations.
                </p>
                <Button variant="primary" href="#contact">
                  Discuss Your Needs
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {targetOrganizations.map((org) => (
                  <div 
                    key={org}
                    className="flex items-center gap-2 p-3 rounded-lg bg-bg-primary/50"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-apple-blue" />
                    <span className="text-body-s text-text-secondary">{org}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollReveal>
      </Container>
    </Section>
  );
};

export default AdvisoryServices;
